import { debugLog } from './logger';
import type { GenericAction } from '../../types/actions';

import { encode } from '@msgpack/msgpack';
import { keccak256, hexToBytes, concatBytes } from 'viem';

/**
 * Convert nonce or expiresAfter to 8-byte big-endian representation
 */
function toUint64Bytes(n: number): Uint8Array {
  const bytes = new Uint8Array(8);
  new DataView(bytes.buffer).setBigUint64(0, BigInt(n));
  return bytes;
}

/**
 * Convert address string to bytes (remove 0x prefix and convert to Uint8Array)
 */
function addressToBytes(address: string): Uint8Array {
  const hex = address.startsWith('0x') ? address.slice(2) : address;
  return hexToBytes(`0x${hex}` as `0x${string}`);
}

/**
 * Create L1 action hash for Hyperliquid orders
 * This implementation matches @nktkas/hyperliquid SDK EXACTLY
 *
 * @see https://github.com/nktkas/hyperliquid/blob/main/src/signing/mod.ts
 */
export function createL1ActionHash(
  action: GenericAction,
  nonce: number,
  vaultAddress?: string,
  expiresAfter?: number
): `0x${string}` {
  debugLog('[Action Hash] Input action:', JSON.stringify(action, null, 2));
  debugLog('[Action Hash] Input nonce:', nonce);
  debugLog('[Action Hash] VaultAddress:', vaultAddress);
  debugLog('[Action Hash] ExpiresAfter:', expiresAfter);

  // 1. Encode action with msgpack (ignoreUndefined is critical!)
  const actionBytes = encode(action, { ignoreUndefined: true });
  debugLog('[Action Hash] Encoded action bytes length:', actionBytes.length);
  debugLog('[Action Hash] First 50 bytes:', Array.from(actionBytes.slice(0, 50)));

  // 2. Convert nonce to 8-byte big-endian
  const nonceBytes = toUint64Bytes(nonce);

  // 3. Vault address handling
  const vaultMarker = vaultAddress ? new Uint8Array([1]) : new Uint8Array([0]);
  const vaultBytes = vaultAddress ? addressToBytes(vaultAddress) : new Uint8Array();

  // 4. Expires after handling
  const expiresMarker = expiresAfter !== undefined ? new Uint8Array([0]) : new Uint8Array();
  const expiresBytes = expiresAfter !== undefined ? toUint64Bytes(expiresAfter) : new Uint8Array();

  // 5. Concatenate all bytes in exact order
  const bytes = concatBytes([
    new Uint8Array(actionBytes),
    nonceBytes,
    vaultMarker,
    vaultBytes,
    expiresMarker,
    expiresBytes,
  ]);

  // 6. Compute keccak256 hash
  return keccak256(bytes);
}
